import { TimelineStage } from '../components/OrderTimeline';

export interface OrderTimelineData {
  orderId: string;
  orderCreatedAt: string;
  dueDate: string;
  currentStatus: string;
  pickupDate?: string;
  completionDate?: string;
}

export const generateTimelineStages = (orderData: OrderTimelineData): TimelineStage[] => {
  const { orderCreatedAt, dueDate, currentStatus, pickupDate, completionDate } = orderData;
  
  // Calculate automatic due date (7 days from creation if not specified)
  const createdDate = new Date(orderCreatedAt);
  const autoDueDate = new Date(createdDate.getTime() + (7 * 24 * 60 * 60 * 1000));
  const finalDueDate = dueDate || autoDueDate.toISOString().split('T')[0];
  
  // Calculate stage dates based on order creation and due date
  const dueDateTime = new Date(finalDueDate);
  const totalDuration = dueDateTime.getTime() - createdDate.getTime();
  const stageDuration = totalDuration / 4; // Divide timeline into 4 stages
  
  const stage1Date = new Date(createdDate.getTime() + stageDuration);
  const stage2Date = new Date(createdDate.getTime() + (stageDuration * 2));
  const stage3Date = new Date(createdDate.getTime() + (stageDuration * 3));
  
  // Determine current stage based on status
  let currentStageIndex = 0;
  switch (currentStatus) {
    case 'pending':
      currentStageIndex = 0;
      break;
    case 'in_progress':
      currentStageIndex = 1;
      break;
    case 'ready':
      currentStageIndex = 2;
      break;
    case 'completed':
      currentStageIndex = 3;
      break;
    default:
      currentStageIndex = 0;
  }
  
  const stages: TimelineStage[] = [
    {
      id: 'order_created',
      title: 'Order Created',
      description: 'Your order has been successfully placed and is now in our system',
      status: currentStageIndex >= 0 ? 'completed' : 'pending',
      completedAt: orderCreatedAt,
      estimatedDate: orderCreatedAt
    },
    {
      id: 'work_in_progress',
      title: 'Kapde is being worked on',
      description: 'Our skilled tailors are working on your garment. This includes cutting, stitching, and initial fittings.',
      status: currentStageIndex === 1 ? 'current' : currentStageIndex > 1 ? 'completed' : 'pending',
      completedAt: currentStageIndex > 1 ? stage1Date.toISOString() : undefined,
      estimatedDate: stage1Date.toISOString()
    },
    {
      id: 'packing',
      title: 'Order is being packed',
      description: 'Your completed garment is being carefully packed and prepared for pickup or delivery',
      status: currentStageIndex === 2 ? 'current' : currentStageIndex > 2 ? 'completed' : 'pending',
      completedAt: currentStageIndex > 2 ? stage2Date.toISOString() : undefined,
      estimatedDate: stage2Date.toISOString()
    },
    {
      id: 'ready_for_pickup',
      title: 'Order is ready for pickup or delivery',
      description: 'Your order is ready! You can pick it up from our store or we can deliver it to you',
      status: currentStageIndex === 3 ? 'current' : currentStageIndex > 3 ? 'completed' : 'pending',
      completedAt: pickupDate || (currentStageIndex > 3 ? stage3Date.toISOString() : undefined),
      estimatedDate: stage3Date.toISOString()
    },
    {
      id: 'completed',
      title: 'Order is complete',
      description: 'Order has been successfully picked up or delivered. Thank you for choosing our services!',
      status: currentStageIndex === 4 ? 'current' : currentStageIndex > 4 ? 'completed' : 'pending',
      completedAt: completionDate || (currentStageIndex > 4 ? dueDateTime.toISOString() : undefined),
      estimatedDate: dueDateTime.toISOString()
    }
  ];
  
  return stages;
};

export const updateTimelineStage = (
  stages: TimelineStage[],
  stageId: string,
  newStatus: 'completed' | 'current' | 'pending'
): TimelineStage[] => {
  return stages.map(stage => {
    if (stage.id === stageId) {
      return {
        ...stage,
        status: newStatus,
        completedAt: newStatus === 'completed' ? new Date().toISOString() : stage.completedAt
      };
    }
    return stage;
  });
};

export const getTimelineProgress = (stages: TimelineStage[]): number => {
  const completedStages = stages.filter(stage => stage.status === 'completed').length;
  return Math.round((completedStages / stages.length) * 100);
};

export const getNextStage = (stages: TimelineStage[]): TimelineStage | null => {
  const currentStageIndex = stages.findIndex(stage => stage.status === 'current');
  if (currentStageIndex === -1 || currentStageIndex === stages.length - 1) {
    return null;
  }
  return stages[currentStageIndex + 1];
};

export const getCurrentStage = (stages: TimelineStage[]): TimelineStage | null => {
  return stages.find(stage => stage.status === 'current') || null;
};
